import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import floatImage from "@/assets/river-kayak-family.webp";

interface RiverHighlightSectionProps {
  ctaLabel?: string;
  onCtaClick?: () => void;
}

const RiverHighlightSection = ({ ctaLabel, onCtaClick }: RiverHighlightSectionProps = {}) => {
  const stats = [
    { value: "72°F", label: "Spring-Fed Water" },
    { value: "1.5", label: "Mile Float" },
    { value: "Unlimited", label: "Tubing & Kayaking" },
    { value: "Included", label: "Shuttle Service" },
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="space-y-6">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              1.5 Miles of Clear, Family-Friendly River
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The San Marcos River is spring-fed, flowing cool and clear all summer long, even in the harshest of droughts. Enjoy a gentle, relaxing float through beautiful cypress and pecan trees. Perfect for all ages. Tube or kayak as many times as you want with our unlimited wristband, and our friendly shuttles bring you right back to do it again.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className="text-center p-4 bg-background rounded-lg shadow-sm border border-border/50"
                >
                  <div className="font-heading text-2xl md:text-3xl lg:text-xl font-bold text-river">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA and Price */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
              {onCtaClick ? (
                <Button variant="cta" size="lg" onClick={onCtaClick}>
                  {ctaLabel ?? "Learn About Tubing & Kayaking"}
                </Button>
              ) : (
                <Link to="/day-rentals#tubing-kayaking">
                  <Button variant="cta" size="lg">
                    {ctaLabel ?? "Learn About Tubing & Kayaking"}
                  </Button>
                </Link>
              )}
              <div className="text-center sm:text-left">
                <span className="font-heading text-2xl font-bold text-copper">$29.99</span>
                <span className="text-muted-foreground ml-2">per person for unlimited float access</span>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <img
              src={floatImage}
              alt="Families floating on the crystal-clear San Marcos River"
              className="rounded-2xl shadow-xl w-full h-auto object-cover"
              loading="lazy"
              decoding="async"
              width={600}
              height={400}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default RiverHighlightSection;
